import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SignIn, SignedIn, SignedOut } from '@clerk/clerk-react';
import Layout from './components/Layout.jsx';
import { ClientsProvider } from './context/ClientsContext.jsx';
import { ClientFilterProvider } from './context/ClientFilterContext.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import FeedbackWidget from './components/FeedbackWidget.jsx';
import { ColTooltipProvider } from './components/shared/ColTooltip.jsx';
import { HelpProvider } from './components/shared/HelpContext.jsx';
import BetterOnDesktop from './components/mobile/BetterOnDesktop.jsx';
import { useIsMobile } from './hooks/useIsMobile.js';
import { ROUTES } from './routes.js';
import { SearchProvider } from './context/SearchContext.jsx';

function LocationBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

function RouteElement({ Component, desktopOnly, label }) {
  const isMobile = useIsMobile();
  if (desktopOnly && isMobile) return <BetterOnDesktop pageName={label} />;
  return <Component />;
}

const BYPASS = import.meta.env.VITE_BYPASS_AUTH === 'true';

function PageFallback() {
  return <div className="flex-1 flex items-center justify-center text-sm text-n-400">Loading…</div>;
}

function AuthenticatedApp() {
  return (
    <ColTooltipProvider>
    <HelpProvider>
    <ClientsProvider>
      <ClientFilterProvider>
      <SearchProvider>
      <Layout>
        <LocationBoundary>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/clients" replace />} />
              {ROUTES.map(({ path, component: Component, desktopOnly, label }) => (
                <Route
                  key={path}
                  path={path}
                  element={<RouteElement Component={Component} desktopOnly={desktopOnly} label={label} />}
                />
              ))}
            </Routes>
          </Suspense>
        </LocationBoundary>
        <FeedbackWidget />
      </Layout>
      </SearchProvider>
      </ClientFilterProvider>
    </ClientsProvider>
    </HelpProvider>
    </ColTooltipProvider>
  );
}

export default function App() {
  if (BYPASS) {
    return (
      <BrowserRouter>
        <AuthenticatedApp />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <SignedOut>
        <div className="min-h-screen bg-sitruna-black flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-sitruna-yellow rounded" />
                <span className="text-white text-xl font-semibold tracking-tight">Sitruna Hub</span>
              </div>
              <p className="text-n-400 text-sm">Internal agency management</p>
            </div>
            <SignIn routing="hash" />
          </div>
        </div>
      </SignedOut>

      <SignedIn>
        <AuthenticatedApp />
      </SignedIn>
    </BrowserRouter>
  );
}
